import _ from "lodash"
import { useCallback } from "react"
import { useDrag, useDrop } from "react-dnd"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { homePageConfigDefault, homePageConfigDicState, HomePageConfigItemType, useHomePageConfiguration, useSetHomePageConfig } from "./homePageSupport"
import { keyHomePageSegmentsHandler, SegmentOrderKeyType } from "./homeStatus"

const ItemTypes = {
    HOME_SECTION: 'homeSection'
}

export type SegmentOrderItemType = [SegmentOrderKeyType, number]

export const logorder = (hpc: HomePageConfigItemType[]) => hpc.reduce((p,c) => `${p} => ${c.config.name}/${c.config.order}`, '')

export const useHomePageReorder = (title: SegmentOrderKeyType, draggingOpacity: number = 0) => {
    const homePageConfig = useHomePageConfiguration()
    const setHomePageConfig = useSetHomePageConfig()
    const hpcDic = useRecoilValue(homePageConfigDicState)
    const setHpshKey = useSetRecoilState(keyHomePageSegmentsHandler)

    const reorder = useCallback((from: SegmentOrderKeyType, to: SegmentOrderKeyType) => {
        // console.log(`%creorder: ${from} => ${to}`, "color: red, font-weight: bold")
        const findSegmentOrder = (name: SegmentOrderKeyType) => homePageConfig.find(x => x.config.name === name)!.config.order - 1
        if (from !== to) {
            const fromIndex = findSegmentOrder(from)
            const toIndex = findSegmentOrder(to)
            // console.log(`${from} -> ${to} # ${fromIndex} -> ${toIndex}`)
            if (fromIndex !== toIndex && (fromIndex > toIndex || fromIndex < (toIndex - 1))) {
                // Prima cosa creiamo un'array ordinata con l'ordine primitivo
                const orderedSegmentArray = _.cloneDeep(homePageConfig).sort((a, b) => a.config.order - b.config.order)
                const elimina = orderedSegmentArray.splice(fromIndex, 1)
                if (fromIndex < toIndex) {
                    orderedSegmentArray.splice(toIndex - 1, 0, elimina[0])
                } else {
                    orderedSegmentArray.splice(toIndex, 0, elimina[0])
                }
                // Rimettiamo tutto in ordine
                // 1. riassegnamo gli indici
                orderedSegmentArray.forEach((x, index) => x.config.order = index + 1)
                // 2. reimpostiamo l'atomo che contiene lo stato e scriviamo nel local storage
                setHomePageConfig(orderedSegmentArray)
                setHpshKey(current => current + 1)
            }
        }
    }, [homePageConfig, setHomePageConfig, setHpshKey])

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: ItemTypes.HOME_SECTION,
        item: ({ title }),
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        })
    }))

    const [{ isOver, dragItem }, drop] = useDrop(
        () => ({
            accept: ItemTypes.HOME_SECTION,
            drop: (item: { title: SegmentOrderKeyType }) => {
                // console.log(`dropping ${JSON.stringify(item)} on ${title}`)
                reorder(item.title, title)
                return ({ title })
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                dragItem: monitor.getItem(),
            }),
        }),
        [],
    )

    const opacity = isDragging ? draggingOpacity : 1
    const dropSegment = title === "Drop"
    const dragItemIndex = dragItem?.title ? hpcDic[dragItem.title as SegmentOrderKeyType].config.order : -1
    const isRightOver = dropSegment ? (isOver && (dragItemIndex !== homePageConfigDefault.length - 1)) : isOver
    const order = hpcDic[title].config.order

    return {
        dragItem,
        isDragging,
        isOver,
        drag,
        preview,
        drop,
        order,
        opacity,
        dropSegment,
        dragItemIndex,
        isRightOver,
    }
}
